import { createContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import type { User } from '../entities/User'
import { apiGetJson, apiPostJson, setUnauthorizedCallback } from '../../../lib/apiClient'

type MinimalSession = { user: { id: string } }

export interface AuthContextValue {
    user: User | null
    session: MinimalSession | null
    loading: boolean
    signIn: (identity: string, password: string, captchaToken?: string, rememberMe?: boolean) => Promise<{ error: any }>
    signUp: (email: string, password: string, username?: string, fullName?: string, zaloPhone?: string, captchaToken?: string) => Promise<{ data: any; error: any }>
    signOut: () => Promise<void>
    resetPassword: (email: string) => Promise<{ error: any; data: any }>
    refreshProfile: () => void
    retryCount: number
    isRetrying: boolean
    connectAttempt: number
    connectMaxAttempts: number
    connectError: string | null
    isInitializing: boolean
    authErrorOpen: boolean
    authToastCountdown: number
}

export const AuthContext = createContext<AuthContextValue | null>(null)

const MAX_ME_ATTEMPTS = 1
const REFERRER_STORAGE_KEY = 'digigo_referrer_id'
const UUID_V4_LIKE_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<MinimalSession | null>(null)
    const [loading, setLoading] = useState(true)
    const [isInitializing, setIsInitializing] = useState(true)
    const [retryCount, setRetryCount] = useState(0)
    const [isRetrying, setIsRetrying] = useState(false)
    const [connectAttempt, setConnectAttempt] = useState(0)
    const [connectError, setConnectError] = useState<string | null>(null)

    // Auth expiration toast state
    const [authErrorOpen, setAuthErrorOpen] = useState(false)
    const [authToastCountdown, setAuthToastCountdown] = useState(3)

    const isFetchingProfile = useRef(false)
    const lastUserId = useRef<string | null>(null)

    const fetchMe = useCallback(async (currentRetry = 0) => {
        if (isFetchingProfile.current && currentRetry === 0) return

        try {
            isFetchingProfile.current = true
            setConnectError(null)
            setIsRetrying(true)
            setRetryCount(currentRetry)
            setConnectAttempt(currentRetry + 1)

            const resp = await apiGetJson<any>('/api/auth/me')

            if (!resp?.success) {
                setUser(null)
                setSession(null)
                setIsRetrying(false)
                setRetryCount(0)
                setConnectAttempt(0)
                setConnectError(null)
                setLoading(false)
                setIsInitializing(false)
                return
            }

            const rawData = Array.isArray((resp as any)?.data) ? (resp as any).data?.[0] : (resp as any)?.data
            const root = rawData || (resp as any)?.profile || (resp as any)?.user
            const profile = (root?.profile || root?.user || root) as User | null
            const userId = String((profile as any)?.id || (root as any)?.id || '')

            // Auth is cookie-based, no token extraction needed

            if (profile) {
                setUser(profile)
            } else {
                setUser(null)
            }

            if (userId) {
                setSession({ user: { id: userId } })
                lastUserId.current = userId
            } else {
                setSession(null)
                lastUserId.current = null
            }

            // Reset retry counters on success
            setIsRetrying(false)
            setRetryCount(0)
            setConnectAttempt(0)
            setConnectError(null)

            setLoading(false)
            setIsInitializing(false)

        } catch (error: any) {
            console.error(`Error fetching me (Attempt ${currentRetry + 1}):`, error)

            if (currentRetry < MAX_ME_ATTEMPTS - 1) {
                await new Promise((resolve) => setTimeout(resolve, 2000))
                await fetchMe(currentRetry + 1)
                return
            } else {
                setConnectError(null)
                setIsRetrying(false)
            }
        } finally {
            isFetchingProfile.current = false
            if (currentRetry === 0 || currentRetry >= MAX_ME_ATTEMPTS - 1 || connectError) {
                setLoading(false)
                setIsInitializing(false)
            }
        }
    }, [connectError])

    useEffect(() => {
        let isMounted = true

        void (async () => {
            if (!isMounted) return

            // Auth is cookie-based, no token to load from localStorage

            try {
                const params = new URLSearchParams(window.location.search)
                const candidate = (params.get('referrer_id') || params.get('ref') || '').trim()
                if (candidate && UUID_V4_LIKE_REGEX.test(candidate)) {
                    localStorage.setItem(REFERRER_STORAGE_KEY, candidate)
                }
            } catch (e) {
                console.warn('Failed to capture referrer id:', e)
            }

            await fetchMe(0)
        })()

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void fetchMe(0)
            }
        }
        window.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            isMounted = false
            window.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [fetchMe])

    useEffect(() => {
        const onRefreshRequested = () => {
            void fetchMe(0)
        }
        window.addEventListener('digigo-auth-refresh', onRefreshRequested)
        return () => window.removeEventListener('digigo-auth-refresh', onRefreshRequested)
    }, [fetchMe])

    const signIn = async (identity: string, password: string, captchaToken?: string, rememberMe?: boolean) => {
        try {
            const resp = await apiPostJson<any>('/api/auth/login', { identity, password, captchaToken, remember_me: rememberMe })

            // Cookies are set by the server response
            await fetchMe(0)
            return { error: null as any }
        } catch (e: any) {
            return { error: { message: e?.message || 'Đăng nhập thất bại' } as any }
        }
    }

    const signUp = async (email: string, password: string, username?: string, fullName?: string, zaloPhone?: string, captchaToken?: string) => {
        try {
            let referrerId: string | undefined
            try {
                const params = new URLSearchParams(window.location.search)
                const fromQuery = (params.get('referrer_id') || params.get('ref') || '').trim()
                const fromStorage = (localStorage.getItem(REFERRER_STORAGE_KEY) || '').trim()
                const candidate = fromQuery || fromStorage
                if (candidate && UUID_V4_LIKE_REGEX.test(candidate)) {
                    referrerId = candidate
                    localStorage.setItem(REFERRER_STORAGE_KEY, candidate)
                }
            } catch (e) {
                console.warn('Failed to read referrer id:', e)
            }

            const resp = await apiPostJson<any>('/api/auth/register', {
                email,
                password,
                username,
                full_name: fullName,
                zalo_phone: zaloPhone,
                referrer_id: referrerId,
                captchaToken,
            })

            // Cookies are set by the server response

            if (resp?.success) await fetchMe(0)

            return { data: resp as any, error: null as any }
        } catch (e: any) {
            return { data: null as any, error: { message: e?.message || 'Đăng ký thất bại' } as any }
        }
    }

    const signOut = async () => {
        try {
            await apiPostJson<{ success: boolean }>('/api/auth/logout', {})
        } catch (e) {
            console.warn('Logout API failed:', e)
        }
        setUser(null)
        setSession(null)
        lastUserId.current = null
        // Cookies are cleared by the server logout response
    }

    // Setup global 401 callback for auto-logout when refresh token fails
    const signOutRef = useRef(signOut)
    signOutRef.current = signOut

    useEffect(() => {
        setUnauthorizedCallback(() => {
            setAuthErrorOpen(true)
            setAuthToastCountdown(3)
            let count = 3
            const timer = setInterval(() => {
                count--
                setAuthToastCountdown(count)
                if (count <= 0) {
                    clearInterval(timer)
                    signOutRef.current()
                }
            }, 1000)
        })

        return () => {
            setUnauthorizedCallback(() => { })
        }
    }, [])

    const resetPassword = async (email: string) => {
        try {
            const resp = await apiPostJson<{ success: boolean; method?: string; message?: string }>('/api/auth/forgot-password', { email })
            return {
                error: null as any,
                data: resp as { success: boolean; method?: string; message?: string }
            }
        } catch (e: any) {
            return { error: { message: e?.message || 'Gửi email khôi phục thất bại' } as any, data: null }
        }
    }

    const value: AuthContextValue = {
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        refreshProfile: () => void fetchMe(0),
        retryCount,
        isRetrying,
        connectAttempt,
        connectMaxAttempts: MAX_ME_ATTEMPTS,
        connectError,
        isInitializing,
        authErrorOpen,
        authToastCountdown,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
